import React from 'react';
import _ from 'underscore';

import { RouterError } from '../../../../bootstrap/routerHelpers/RouterError';
import { SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { Sites } from '../../../../siteManagement/services/resources/Sites';
import { GeneralServerSettingsCoordinator } from '../../../general-settings/components/server-settings/GeneralServerSettingsCoordinator';
import { IEditableServerSettings } from '../../../ServerApi/types/IEditableServerSettings';
import { IServerTimestamps, ServerSettings } from '../../../services/ServerSettings';
import { ISiteSettingsForServerAdmin } from '../../../types/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../types/ISiteSettingsForSiteAdmin';

export interface GeneralServerSettingsSubspaceOwnProps {
  readonly server: SessionManagementService;
}

export interface GeneralServerSettingsSubspaceServicesProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
}

export type GeneralServerSettingsSubspaceProps = GeneralServerSettingsSubspaceOwnProps & GeneralServerSettingsSubspaceServicesProps;

export interface GeneralServerSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly serverTimestamps: IServerTimestamps;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
}

export class GeneralServerSettingsSubspace extends React.PureComponent<GeneralServerSettingsSubspaceProps, GeneralServerSettingsSubspaceState> {

  private unmounted: boolean = false;

  public constructor(props: GeneralServerSettingsSubspaceProps) {
    super(props);
    this.state = {
      hasDataToRender: false,
      hasError: false,
      serverSettings: undefined,
      serverTimestamps: undefined,
      siteSettings: undefined,
      siteSettingsForServerAdmin: undefined};
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!_.isUndefined(this.state.serverSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.serverTimestamps)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'server-default'} urls={undefined}/>;
    }

    return (
      <div className={'tb-stylish-settings'}>
        <GeneralServerSettingsCoordinator {...this.state}/>
      </div>
    );
  }

  private updateState<K extends keyof GeneralServerSettingsSubspaceState>(newState: Pick<GeneralServerSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getServerSettings();
    this.getServerTimestamps();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
  }

  private getServerSettings = async () => {
    try {
      const serverSettings = await this.props.serverSettingsService.fetch();
      this.updateState({serverSettings: serverSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getServerTimestamps = async () => {
    try {
      const serverTimestamps = await this.props.serverSettingsService.getTimestamps();
      this.updateState({serverTimestamps});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getSiteSettingsForServerAdmin = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const serverSettings = await this.props.sites.getCurrentSiteSettingsForServerAdmin();
      this.updateState({siteSettingsForServerAdmin: serverSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };
}
